<template>
  <div v-if="isOpen" class="modal-overlay">
    <div class="modal-container">
      <div class="modal-header">
        <h3>
          {{ editMode ? "Edit Purchase Order" : "Create Purchase Order" }}
        </h3>
        <button @click="closeModal">&times;</button>
      </div>

      <div class="modal-body">
        <form @submit.prevent="saveOrder">
          <div class="relative w-full mb-3">
            <label class="block text-blueGray-600 text-xs mb-2">
              Item Description (Description of the item to buy)
            </label>
            <input
              v-model="form.description"
              type="text"
              class="border rounded w-full px-3 py-2"
              placeholder="Enter item description"
              required
            />
          </div>

          <div class="relative w-full mb-3">
            <label class="block text-blueGray-600 text-xs mb-2">
              Item Link (website where the item can be purchased)
            </label>
            <input
              v-model="form.link"
              type="url"
              class="border rounded w-full px-3 py-2"
              placeholder="Enter item link"
            />
          </div>

          <div class="relative w-full mb-3">
            <label class="block text-blueGray-600 text-xs mb-2">
              Price (Item price from the website link)
            </label>
            <input
              type="text"
              v-model="displayPrice"
              @input="handleTyping"
              @blur="formatPrice"
              class="border rounded w-full px-3 py-2"
              placeholder="$0.00"
              required
            />
          </div>

          <select
            v-model="selectedService"
            @change="updateServiceSelection"
            class="border rounded w-full px-3 py-2"
            required
          >
            <option :value="null" disabled>Select a service code</option>
            <option
              v-for="code in serviceCodes"
              :key="code.client_spending_plan_items_id"
              :value="code"
            >
              {{ code.code }} - {{ code.service_code_description }}
            </option>
          </select>

          <!-- Shipping Address -->
          <div class="relative w-full mb-3">
            <label class="block text-blueGray-600 text-xs mb-2">
              Shipping Address
            </label>
            <input
              v-model="form.shipping_address"
              ref="addressInput"
              type="text"
              class="border rounded w-full px-3 py-2"
              :disabled="!differentAddress"
              placeholder="Enter alternate shipping address"
            />

            <div class="mt-2 flex items-center">
              <input
                id="diff-address"
                type="checkbox"
                v-model="differentAddress"
                class="mr-2"
              />
              <label for="diff-address" class="text-sm text-blueGray-600">
                Different from current address
              </label>
            </div>
          </div>

          <div class="text-right mt-6">
            <button
              type="button"
              class="bg-gray-300 text-gray-700 px-4 py-2 rounded mr-2 hover:bg-gray-400"
              @click="closeModal"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="bg-lightBlue-600 text-blue px-4 py-2 rounded hover:bg-blue-700"
            >
              {{ editMode ? "Update" : "Save" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
/* global google */

export default {
  name: "PurchaseOrderModal",
  props: {
    isOpen: Boolean,
    editMode: Boolean,
    orderData: Object,
  },
  data() {
    return {
      form: {
        id: null,
        description: "",
        link: "",
        price: "",
        status: "Pending",
        shipping_address: "",
        client_spending_plan_items_id: "",
        client_spending_plan_id: "",
        service_code: "",
        service_code_id: "",
      },
      client_data: {},
      differentAddress: false,
      autocomplete: null,
      sessionToken: null,
      serviceCodes: [],
      displayPrice: "",
    };
  },
  mounted() {
    this.fetchInformation();
    this.fetchServiceCodes();
    this.loadGoogleMaps();
    const interval = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places) {
        clearInterval(interval);
        this.initAutocomplete();
      }
    }, 500);
  },
  watch: {
    orderData: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // ✅ Populate form
          this.form = {
            id: newVal.id || null,
            description: newVal.description || "",
            link: newVal.link || "",
            price: newVal.price || "",
            shipping_address: newVal.shipping_address || "",
            status: newVal.status || "Pending",
            service_code_id: newVal.service_code_id || "",
            service_code: newVal.service_code || "",
            client_spending_plan_id: newVal.client_spending_plan_id || "",
            client_spending_plan_items_id:
              newVal.client_spending_plan_items_id || "",
          };

          // ✅ Set displayed price (formatted)
          this.displayPrice =
            "$" +
            Number(this.form.price).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });

          // ✅ Find and preselect matching service code
          const match = this.serviceCodes.find(
            (c) => c.service_code_id === newVal.service_code_id
          );
          this.selectedService = match || null;

          this.differentAddress =
            newVal.shipping_address &&
            newVal.shipping_address !==
              `${this.client_data.address}, ${this.client_data.city}, ${this.client_data.state} ${this.client_data.zip_code}`;
        } else {
          // Reset form when creating new
          this.form = {
            id: null,
            description: "",
            link: "",
            price: "",
            status: "Pending",
            shipping_address: "",
            client_spending_plan_items_id: "",
            client_spending_plan_id: "",
            service_code: "",
            service_code_id: "",
          };
          this.selectedService = null;
          this.displayPrice = "";
        }
      },
    },
    differentAddress(newVal) {
      if (!newVal && this.client_data.address) {
        const { address, city, state, zip_code } = this.client_data;
        this.form.shipping_address = `${address}, ${city}, ${state} ${zip_code}`;
      } else {
        this.form.shipping_address = "";
        // Initialize autocomplete only when checked
        if (newVal && window.google) {
          this.initAutocomplete();
        }
      }
    },
  },
  methods: {
    updateServiceSelection() {
      if (this.selectedService) {
        this.form.service_code = this.selectedService.code;
        this.form.client_spending_plan_items_id =
          this.selectedService.client_spending_plan_items_id;
        this.form.client_spending_plan_id =
          this.selectedService.client_spending_plan_id;
        this.form.service_code_id = this.selectedService.service_code_id;
      } else {
        this.form.service_code = "";
        this.form.client_spending_plan_items_id = "";
        this.form.client_spending_plan_id = "";
      }
    },
    handleTyping(e) {
      let value = e.target.value.replace(/[^0-9.]/g, "");

      // Only allow one dot and two decimals
      const parts = value.split(".");
      if (parts.length > 2) parts.splice(2);
      if (parts[1]) parts[1] = parts[1].slice(0, 2);
      value = parts.join(".");

      this.form.price = value ? parseFloat(value) : 0;
      this.displayPrice = value; // Show raw number while typing
    },

    // On blur, format to $X,XXX.XX
    formatPrice() {
      if (!this.form.price || isNaN(this.form.price)) {
        this.displayPrice = "";
        return;
      }

      this.displayPrice =
        "$" +
        Number(this.form.price).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
    },
    closeModal() {
      this.$emit("close");
    },
    async fetchInformation() {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch_client_information`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await response.json();
        if (result.client) {
          this.client_data = {
            ...result.client,
            ein_number: result.client.ein_number
              ? String(result.client.ein_number)
              : "",
          };
          if (!this.differentAddress) {
            const { address, city, state, zip_code } = this.client_data;
            this.form.shipping_address = `${address}, ${city}, ${state} ${zip_code}`;
          }
        }
      } catch (error) {
        console.error("Error fetching client info:", error);
      }
    },
    saveOrder() {
      if (!this.form.description || this.form.price <= 0) {
        alert("Please complete required fields.");
        return;
      }
      this.form.status = "Pending";
      this.$emit("save", { ...this.form });
      this.closeModal();
    },

    // ✅ Load Google Maps API once
    loadGoogleMaps() {
      if (window.google && window.google.maps) return;
      const script = document.createElement("script");
      script.src =
        "https://maps.googleapis.com/maps/api/js?key=AIzaSyCLzpbROUixmOarMrMHlYo8CINL0c61HuQ&libraries=places";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (this.differentAddress) this.initAutocomplete();
      };
      document.head.appendChild(script);
    },

    // ✅ Initialize Autocomplete restricted to California, USA
    initAutocomplete() {
      const input = this.$refs.addressInput;
      if (!input) return;

      // ✅ Create a session token
      this.sessionToken = new google.maps.places.AutocompleteSessionToken();

      // ✅ Initialize Autocomplete with token
      this.autocomplete = new google.maps.places.Autocomplete(input, {
        types: ["address"],
        componentRestrictions: { country: "us" },
        fields: ["formatted_address", "address_components", "geometry"],
        sessionToken: this.sessionToken, // <-- important
      });

      const californiaBounds = new google.maps.LatLngBounds(
        { lat: 32.5343, lng: -124.4096 },
        { lat: 42.0095, lng: -114.1308 }
      );
      this.autocomplete.setBounds(californiaBounds);
      this.autocomplete.setOptions({ strictBounds: true });

      const observer = new MutationObserver(() => {
        const pacContainer = document.querySelector(".pac-container");
        const modalContainer = this.$el.querySelector(".modal-container");
        if (
          pacContainer &&
          modalContainer &&
          !modalContainer.contains(pacContainer)
        ) {
          modalContainer.appendChild(pacContainer);
          pacContainer.style.zIndex = "99999";
          pacContainer.style.position = "absolute";
          pacContainer.style.background = "#fff";
          pacContainer.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
          observer.disconnect();
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      this.autocomplete.addListener("place_changed", () => {
        const place = this.autocomplete.getPlace();
        if (place && place.formatted_address) {
          this.form.shipping_address = place.formatted_address;

          // ✅ Once a place is selected, end session and create a new one
          this.sessionToken = new google.maps.places.AutocompleteSessionToken();
          this.autocomplete.setOptions({ sessionToken: this.sessionToken });
        }
      });
    },

    onPlaceChange(event) {
      const place = event.target.value;
      console.log("Selected address:", place);
      this.form.shipping_address = place;
    },
    async fetchServiceCodes() {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/service_codes`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await response.json();
        if (Array.isArray(result)) {
          this.serviceCodes = result;
        } else if (result.data) {
          this.serviceCodes = result.data;
        }
      } catch (error) {
        console.error("Error fetching service codes:", error);
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}
.modal-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.modal-header h3 {
  margin: 0;
}
.modal-header button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
}
::v-deep(.pac-container) {
  z-index: 9999 !important;
}
</style>
